// Which list a to-do or project is in, and how each list is grouped and ordered, as the kit's Store has it
// (Store.swift: the smart lists, `sections(for:)`, and their helpers; Models.swift: Project.isActive, Todo.isStale).
// The kit is the reference.
//
// The order within a list is always the manual one (`order`, smallest first), except where a list says otherwise:
// Due by deadline, Stale by when the to-do was added, the Archive by when it was completed (latest first), Find's
// completed ones latest first. Equal keys keep the manual order (the kit's sort is stable).
//
// Situations are not applied on the web (docs/47, decision 7): it can't know the place, and hiding by time alone
// would show a different list than the iPhone beside it. So nothing here hides a to-do, project or area by a
// situation; where the kit asks `isHiddenBySituation` or `isHidden(project)`, the web's answer is always "no".

import { addDays, dayOf, dayParts, daysBetween, maxDay } from "./days";
import type { Area, Birthday, Project, Section, Todo } from "./records";
import { isList } from "./records";
import type { Day } from "./types";

// MARK: - Lists

/** A list the user can open (Models.swift: Route). */
export type Route =
  | { kind: "inbox" } | { kind: "today" } | { kind: "scheduled" } | { kind: "anytime" } | { kind: "someday" }
  | { kind: "archive" }
  /** Everything with a deadline, the most urgent first. */
  | { kind: "due" }
  /** To-dos that belong to no project and no area ("Miscellaneous"). */
  | { kind: "general" }
  | { kind: "project"; id: string }
  | { kind: "area"; id: string }
  /** Everything Find turns up for a text. */
  | { kind: "search"; query: string }
  /** Open to-dos nobody has touched for a while. */
  | { kind: "stale" }
  /** The open to-dos with a reminder at a saved place: an iPhone list; empty on the web. */
  | { kind: "place"; id: string }
  /** The template library and one template: pages of their own, not lists of to-dos. */
  | { kind: "templates" }
  | { kind: "template"; id: string }
  /** Every habit (docs/63): Today | All. A page of its own, like the templates. */
  | { kind: "habits" };

/** The id of Today's habits group (ListSection.todayHabitsID): the screens draw it in lilac. */
export const TODAY_HABITS_ID = "today-habits";

/** The built-in lists in the order Home shows them (Route.builtIn). */
export const BUILT_IN_ROUTES: readonly Route[] = [
  { kind: "inbox" }, { kind: "today" }, { kind: "due" }, { kind: "scheduled" }, { kind: "anytime" }, { kind: "someday" },
  { kind: "stale" }, { kind: "archive" }, { kind: "general" },
];

/** The stable name of a list, for settings per list and for favorites (`{list: {_0: key}}`) (Route.storageKey). */
export function storageKey(route: Route): string {
  switch (route.kind) {
    case "project": case "area": return route.id;
    case "place": return `place-${route.id}`;
    case "template": return `template(${route.id})`;
    default: return route.kind;
  }
}

/** Which completed to-dos a list shows besides the open ones: none, those done today (Today's default), or all. */
export type CompletedShown = "none" | "today" | "all";

/** Without Pro the Archive reaches back this many days; older to-dos are kept, only not shown (Premium.freeHistoryDays). */
export const FREE_HISTORY_DAYS = 30;

/** When a to-do counts as stale, in days: a setting, 30 unless chosen otherwise (Staleness). */
export const STALE_DEFAULT_DAYS = 30;
export const STALE_CHOICES: readonly number[] = [14, 30, 60, 90];

// MARK: - The rules of single records

/** A project that waits for its day, or for someday, keeps its to-dos out of the working lists (Project.isActive). */
export function isActiveProject(project: Pick<Project, "start">, today: Day): boolean {
  switch (project.start?.kind) {
    case "someday": return false;
    case "on": return project.start.day <= today;
    default: return true;
  }
}

/** A list has no deadline; one set while it was a project is kept, hidden (Project.activeDeadline). */
export function activeDeadline(project: Project): Day | undefined {
  return isList(project) ? undefined : project.deadline;
}

/**
 * In Today: scheduled for today or earlier, or due by today unless it waits for someday (Store.isToday). Never an
 * Inbox to-do: what is not decided yet shows in the Inbox alone, whatever deadline it carries (docs/60).
 */
export function isToday(todo: Pick<Todo, "start" | "deadline">, today: Day): boolean {
  if (todo.start.kind === "inbox") return false;
  if (todo.start.kind === "on" && todo.start.day <= today) return true;
  return todo.start.kind !== "someday" && todo.deadline !== undefined && todo.deadline <= today;
}

/**
 * Open, planned for no particular day and due on none (Anytime or Someday, not the Inbox), and added at least `days`
 * days ago, or kept in the Stale review that long ago (Todo.isStale). Counted in whole 24-hour days from now.
 */
export function isStale(todo: Todo, days: number, now: Date): boolean {
  if (todo.completedAt !== undefined || todo.deadline !== undefined) return false;
  if (todo.start.kind !== "anytime" && todo.start.kind !== "someday") return false;
  return (todo.keptAt ?? todo.createdAt).getTime() < now.getTime() - days * 86_400_000;
}

/** Open, starting Someday, in a list that mixes it with others: its row shows a grey title and a leaf (docs/59, 3c;
 *  Todo.marksSomeday(in:) in the kit). Not in Someday itself, where every to-do does. */
export function marksSomeday(todo: Todo, route: Route): boolean {
  return todo.completedAt === undefined && todo.start.kind === "someday" && route.kind !== "someday";
}

/**
 * How long a to-do has waited in Today since the day it was planned for, as its row shows it at the right edge with an
 * hourglass (Todo.waitingDays(in:), docs/65, A6): from two days on; undefined before, in other lists and once done.
 */
export function waitingDays(todo: Todo, route: Route, today: Day): number | undefined {
  if (route.kind !== "today" || todo.completedAt !== undefined || todo.start.kind !== "on" || todo.start.day >= today) return undefined;
  const days = daysBetween(todo.start.day, today);
  return days >= WAITING_SHOWN_FROM ? days : undefined;
}

/** The fewest days of waiting a row shows (Todo.waitingShownFrom). */
export const WAITING_SHOWN_FROM = 2;

/** The day an open to-do starts, if that is after today (Todo.laterStart). */
export function laterStart(todo: Todo, today: Day): Day | undefined {
  return todo.completedAt === undefined && todo.start.kind === "on" && todo.start.day > today ? todo.start.day : undefined;
}

/** How a list arranges its to-dos, a setting per list (ListArrangement). The order itself is always the manual one. */
export interface ListArrangement {
  deadlinesFirst?: boolean;
  hidesScheduled?: boolean;
  hidesSomeday?: boolean;
  hidesStale?: boolean;
  staleAfterDays?: number;
  /** To-dos that start on a later day go to the end, by date. Lists of projects and areas do this. */
  laterLast?: boolean;
}

/** The to-dos as the arrangement shows them (ListArrangement.apply). */
export function arrange(todos: readonly Todo[], arrangement: ListArrangement, today: Day, now: Date): Todo[] {
  const isHidden = (todo: Todo) => {
    if (todo.completedAt !== undefined) return false;
    if (arrangement.hidesStale && isStale(todo, arrangement.staleAfterDays ?? STALE_DEFAULT_DAYS, now)) return true;
    if (todo.start.kind === "someday") return arrangement.hidesSomeday === true;
    if (todo.start.kind === "on") return arrangement.hidesScheduled === true && todo.start.day > today;
    return false;
  };
  // One order in every list (docs/55, item 2): open to-dos in the user's order, then those that start on a later
  // day (where the list puts them last), then the completed ones. Deadlines First reorders the open ones only.
  const visible = todos.filter((todo) => !isHidden(todo));
  const done = visible.filter((todo) => todo.completedAt !== undefined);
  let open = visible.filter((todo) => todo.completedAt === undefined);
  if (arrangement.deadlinesFirst) {
    // To-dos with a deadline come first, the most urgent on top. The rest keep their manual order.
    const dated = open.filter((todo) => todo.deadline !== undefined).sort((a, b) => compareOptional(a.deadline, b.deadline));
    open = [...dated, ...open.filter((todo) => todo.deadline === undefined)];
  }
  if (!arrangement.laterLast) return [...open, ...done];
  const later = open.filter((todo) => laterStart(todo, today) !== undefined)
    .sort((a, b) => compareOptional(laterStart(a, today), laterStart(b, today)));
  return [...open.filter((todo) => laterStart(todo, today) === undefined), ...later, ...done];
}

/**
 * Where the "Later" line goes in a list arranged by `arrange` (ListArrangement.laterLineIndex): the index of the
 * first to-do that starts later; none when nothing does, and none when every open to-do does, since the line would
 * only repeat what the list's heading says (Miscellaneous's "Scheduled" group; docs/55, item 2).
 */
export function laterLineIndex(arranged: readonly Todo[], arrangement: ListArrangement, today: Day): number | undefined {
  if (!arrangement.laterLast) return undefined;
  const index = arranged.findIndex((todo) => laterStart(todo, today) !== undefined);
  if (index < 0) return undefined;
  const open = arranged.filter((todo) => todo.completedAt === undefined);
  return open.every((todo) => laterStart(todo, today) !== undefined) ? undefined : index;
}

/**
 * Whether `arrange` places an open to-do by its dates rather than in the user's order (ListArrangement.placesByDate):
 * by its deadline under Deadlines First, by its start under the "Later" line. A drag offers no place among these,
 * since a drop there would put the to-do somewhere else in the user's order than it shows (Bastian, 2026-10-24).
 */
export function placedByDate(todo: Todo, arrangement: ListArrangement, today: Day): boolean {
  if (todo.completedAt !== undefined) return false;
  return (arrangement.deadlinesFirst === true && todo.deadline !== undefined) ||
    (arrangement.laterLast === true && laterStart(todo, today) !== undefined);
}

/**
 * Whether a list shows a to-do among the completed ones at the end, in the order they were done rather than the user's
 * (ListArrangement.placesAsDone): ticked, and no longer just ticked (`lingering`).
 */
export function placedAsDone(todo: Todo, lingering?: Pick<ReadonlySet<string>, "has">): boolean {
  return todo.completedAt !== undefined && !(lingering?.has(todo.id) ?? false);
}

/**
 * Whether a drag of `dragged` offers a place right above or below `todo` (ListArrangement.offersPlace(beside:dragging:)):
 * not beside what the list places by its dates, nor for a drag that holds any of it; and for open to-dos not beside the
 * completed ones at the end (Bastian, 2026-10-29, Today with a ticked habit): in the user's order a ticked to-do can
 * stand anywhere among the open ones, so a drop above it landed there, rows away from where it was let go. The last
 * place open to-dos get is the end of the open ones, right above the completed. cases/lists.json ("dropPlaces").
 */
export function offersPlaceBeside(todo: Todo, dragged: readonly Todo[], arrangement: ListArrangement, today: Day, lingering?: Pick<ReadonlySet<string>, "has">): boolean {
  if (placedByDate(todo, arrangement, today) || dragged.some((other) => placedByDate(other, arrangement, today))) return false;
  return !placedAsDone(todo, lingering) || dragged.every((other) => placedAsDone(other, lingering));
}

/**
 * Lists that show everything in one place, including what is planned for later (Route.canHideLater): they can hide
 * scheduled and someday to-dos, and put those that start later at the end, under the "Later" line.
 */
export function canHideLater(route: Route): boolean {
  return route.kind === "project" || route.kind === "area" || route.kind === "general";
}

/** Lists that can show their completed to-dos at the end (Route.canShowCompleted): Today and a project, nowhere else. */
export function canShowCompleted(route: Route): boolean {
  return route.kind === "today" || route.kind === "project";
}

/** Lists whose menu offers Deadlines First (Route.canSortDeadlinesFirst): lists in the user's order. */
export function canSortDeadlinesFirst(route: Route): boolean {
  return ["inbox", "today", "anytime", "someday", "general", "project", "area"].includes(route.kind);
}

/** Days in order, an absent one last (as `?? .distantFuture`). */
function compareOptional(a: Day | undefined, b: Day | undefined): number {
  if (a === b) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a < b ? -1 : 1;
}

// MARK: - Birthdays

/** A birthday on its day, for a month in Scheduled. */
export interface BirthdayDay {
  birthday: Birthday;
  day: Day;
}

/** The birthday in a year; 29 February is the 28th in a year without it (Birthday.occurrence(in:)). */
export function birthdayIn(birthday: Birthday, year: number): Day | null {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const date = birthday.month === 2 && birthday.day === 29 && !leap ? 28 : birthday.day;
  const length = new Date(Date.UTC(year, birthday.month, 0)).getUTCDate();
  if (birthday.month < 1 || birthday.month > 12 || date < 1 || date > length) return null;
  return `${String(year).padStart(4, "0")}-${String(birthday.month).padStart(2, "0")}-${String(date).padStart(2, "0")}` as Day;
}

/** The birthday's next day on or after `day` (Birthday.next(after:)). */
export function nextBirthday(birthday: Birthday, day: Day): Day {
  const year = dayParts(day).year;
  for (const candidate of [year, year + 1]) {
    const occurrence = birthdayIn(birthday, candidate);
    if (occurrence !== null && occurrence >= day) return occurrence;
  }
  return birthdayIn(birthday, year + 1) ?? day;
}

// MARK: - The lists of an account

/** A block of to-dos inside a list screen (ListSection). */
export interface ListSection {
  /** Stable within the list, for the screen: readable here, where the kit uses its own. */
  id: string;
  header: ListHeader;
  todos: Todo[];
  /** Projects that belong in this block, such as projects starting on a day. */
  projects: Project[];
}

export type ListHeader =
  | { kind: "none" }
  /** To-dos without project or area, inside a list that groups by project ("Miscellaneous"). */
  | { kind: "general" }
  | { kind: "project"; project: Project }
  | { kind: "area"; area: Area }
  | { kind: "section"; section: Section }
  | { kind: "day"; day: Day }
  /** Scheduled beyond the next week: everything of a month in one block, by date, from this day on. */
  | { kind: "month"; day: Day }
  /** Scheduled: the name of the month above the days of the next week, at the month's first day. */
  | { kind: "monthTitle"; day: Day }
  | { kind: "text"; text: ListText };

/** The texts headers carry, by key; the screens translate them. */
export type ListText =
  | "projects" | "todos" | "completed" | "overdue" | "due" | "upcomingDeadlines" | "today" | "scheduled" | "anytime" | "someday" | "habits";

/** What the Archive is narrowed to (Store.archiveFilter): one project, or one area with its projects. Not stored. */
export interface ArchiveFilter {
  kind: "project" | "area";
  id: string;
}

/** The records the lists are made from. Birthdays only for Scheduled's months. */
export interface ListData {
  todos: readonly Todo[];
  projects: readonly Project[];
  areas: readonly Area[];
  sections: readonly Section[];
  birthdays?: readonly Birthday[];
}

export interface ListOptions {
  /** Today where the browser is. */
  today: Day;
  /** The browser's zone: the day a to-do was completed on. */
  zone: string;
  /** Now, for Stale. */
  now: Date;
  /** Without Pro, `FREE_HISTORY_DAYS`; with Pro, absent (everything). */
  historyDays?: number;
  /** The setting; `STALE_DEFAULT_DAYS` when absent. */
  staleAfterDays?: number;
  /** The setting "Group Today by project and area" (TodayLayout.groupsByProject); off by default. */
  todayGroupsByProject?: boolean;
  /** Habits in a group of their own at the top of Today (TodayLayout.habitsInOwnGroup, docs/63); off by default. */
  habitsInOwnGroup?: boolean;
  /**
   * Today keeps the user's sections when nothing in them is shown (TodayLayout.showsEmptySections, Settings › Lists);
   * off by default: an empty section steps aside.
   */
  todayShowsEmptySections?: boolean;
  /** Today sections made here since the page opened, that no to-do has gone into yet (Store.newTodaySectionIDs). */
  newTodaySections?: ReadonlySet<string>;
  /** Just completed to-dos that stay visible for a moment before they leave the list. */
  lingering?: ReadonlySet<string>;
  /** The Archive narrowed to one project or area while the user looks at what was completed there. */
  archiveFilter?: ArchiveFilter;
}

/** Every list of an account at one moment. Make a new one when the records or the day change. */
export class Lists {
  readonly today: Day;
  private readonly data: ListData;
  private readonly options: ListOptions;
  private readonly projectByID: Map<string, Project>;
  private readonly areaByID: Map<string, Area>;

  constructor(data: ListData, options: ListOptions) {
    this.data = data;
    this.options = options;
    this.today = options.today;
    this.projectByID = new Map(data.projects.map((project) => [project.id, project]));
    this.areaByID = new Map(data.areas.map((area) => [area.id, area]));
  }

  // MARK: Lookup

  project(id: string | undefined): Project | undefined {
    return id === undefined ? undefined : this.projectByID.get(id);
  }

  area(id: string | undefined): Area | undefined {
    return id === undefined ? undefined : this.areaByID.get(id);
  }

  get sortedAreas(): Area[] {
    return [...this.data.areas].sort((a, b) => a.order - b.order);
  }

  /** A project's sections, in their order. */
  sectionsIn(projectID: string | undefined): Section[] {
    if (projectID === undefined) return [];
    return this.data.sections.filter((section) => section.projectID === projectID).sort((a, b) => a.order - b.order);
  }

  /** The sections the user has set up for Today: those without a project. */
  get todaySections(): Section[] {
    return this.data.sections.filter((section) => section.projectID === undefined).sort((a, b) => a.order - b.order);
  }

  /** The Today section that takes the to-dos placed in none, if the user chose one. */
  get defaultTodaySection(): Section | undefined {
    return this.todaySections.find((section) => section.isDefault === true);
  }

  /** The section of a to-do, if it still exists in the to-do's project. */
  sectionOf(todo: Todo): Section | undefined {
    if (todo.projectID === undefined) return undefined;
    return this.data.sections.find((section) => section.id === todo.sectionID && section.projectID === todo.projectID);
  }

  /**
   * A Today section made in this browser since the page opened that no to-do is in (Store.isNewTodaySection): it shows
   * while empty. The data store forgets it once a to-do goes in, so emptied again it steps aside as the others do.
   */
  isNewTodaySection(id: string): boolean {
    return this.options.newTodaySections?.has(id) === true && !this.data.todos.some((todo) => todo.todaySectionID === id);
  }

  /** The Today section of a to-do, if it still exists. */
  todaySectionOf(todo: Todo): Section | undefined {
    return this.todaySections.find((section) => section.id === todo.todaySectionID);
  }

  /** An area's open projects and lists, in their order. */
  projectsIn(area: Area): Project[] {
    return this.data.projects.filter((project) => project.areaID === area.id && project.completedAt === undefined).sort((a, b) => a.order - b.order);
  }

  /** To-dos of a project that has not started are only visible inside the project. */
  isInWaitingProject(todo: Todo): boolean {
    const project = this.project(todo.projectID);
    return project !== undefined && !isActiveProject(project, this.today);
  }

  /** Just completed and still in its place for a moment (the screens keep it among the open ones). */
  isLingering(id: string): boolean {
    return this.options.lingering?.has(id) ?? false;
  }

  /** In no project and no area that exists: in Miscellaneous ("General"). */
  isLoose(todo: Todo): boolean {
    return this.project(todo.projectID) === undefined && this.area(todo.areaID) === undefined;
  }

  isToday(todo: Todo): boolean {
    return isToday(todo, this.today);
  }

  /** Projects waiting for someday, in their order. */
  get somedayProjects(): Project[] {
    return this.data.projects.filter((project) => project.completedAt === undefined && project.start?.kind === "someday")
      .sort((a, b) => a.order - b.order);
  }

  /** Projects waiting for a later day (in the order they are stored, as the kit has them). */
  get scheduledProjects(): Project[] {
    return this.data.projects.filter((project) =>
      project.completedAt === undefined && !isActiveProject(project, this.today) && project.start?.kind !== "someday");
  }

  /** How much of a project is done: completed to-dos of all its to-dos, 0 when it has none. */
  progress(project: Project): number {
    const all = this.data.todos.filter((todo) => todo.projectID === project.id);
    return all.length === 0 ? 0 : all.filter((todo) => todo.completedAt !== undefined).length / all.length;
  }

  /** A project's to-dos, done and in all, as its ring counts them (Store.doneCount(of:), docs/51). */
  doneCount(project: Project): { done: number; total: number } {
    const all = this.data.todos.filter((todo) => todo.projectID === project.id);
    return { done: all.filter((todo) => todo.completedAt !== undefined).length, total: all.length };
  }

  /**
   * Each part of a project with how many of its to-dos are done (Store.sectionProgress(of:)): the to-dos in no section
   * first, only when there are some, then the sections in their order, the empty ones too.
   */
  sectionProgress(project: Project): { id?: string; title?: string; done: number; total: number }[] {
    const sections = this.sectionsIn(project.id);
    const known = new Set(sections.map((section) => section.id));
    const all = this.data.todos.filter((todo) => todo.projectID === project.id);
    const part = (todos: readonly Todo[]) => ({ done: todos.filter((todo) => todo.completedAt !== undefined).length, total: todos.length });
    const loose = all.filter((todo) => todo.sectionID === undefined || !known.has(todo.sectionID));
    return [
      ...(loose.length ? [part(loose)] : []),
      ...sections.map((section) => ({ id: section.id, title: section.title, ...part(all.filter((todo) => todo.sectionID === section.id)) })),
    ];
  }

  /**
   * The deadlines Today's glance names (Store.deadlineGlance(limit:)): every open to-do due today or overdue, then the
   * next ones, `limit` in all; what is due shows however many there are.
   */
  deadlineGlance(limit = 4): { items: Todo[]; more: number } {
    const open = this.due().filter((todo) => todo.completedAt === undefined);
    const today = this.today;
    const late = open.filter((todo) => todo.deadline !== undefined && todo.deadline <= today);
    const coming = open.filter((todo) => todo.deadline !== undefined && todo.deadline > today);
    const items = [...late, ...coming].slice(0, Math.max(limit, late.length));
    return { items, more: open.length - items.length };
  }

  /** The list a to-do is best shown in (Store.route(for:)). */
  routeFor(todo: Todo): Route {
    if (todo.projectID !== undefined) return { kind: "project", id: todo.projectID };
    if (todo.areaID !== undefined) return { kind: "area", id: todo.areaID };
    switch (todo.start.kind) {
      case "inbox": return { kind: "inbox" };
      case "someday": return { kind: "someday" };
      case "anytime": return { kind: "anytime" };
      case "on": return this.isToday(todo) ? { kind: "today" } : { kind: "scheduled" };
    }
  }

  // MARK: The smart lists

  /** Whether a to-do shows in a list that shows `completed` besides the open ones. */
  private isShown(todo: Todo, completed: CompletedShown): boolean {
    if (todo.completedAt === undefined) return true;
    if (this.options.lingering?.has(todo.id)) return true;
    switch (completed) {
      case "none": return false;
      case "today": return dayOf(todo.completedAt, this.options.zone) === this.today;
      case "all": return true;
    }
  }

  /** Every to-do a list may show, in the manual order. (Situations would hide some here on the iPhone.) */
  private shown(completed: CompletedShown): Todo[] {
    return this.data.todos.filter((todo) => this.isShown(todo, completed)).sort((a, b) => a.order - b.order);
  }

  inbox(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => todo.start.kind === "inbox");
  }

  todayTodos(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => this.isToday(todo) && !this.isInWaitingProject(todo) && !this.isEarlierHabitDay(todo));
  }

  /** A habit's day before today: its history (on the habit's page), never in Today, also when ticked today. */
  private isEarlierHabitDay(todo: Todo): boolean {
    return todo.habitID !== undefined && todo.start.kind === "on" && todo.start.day < this.today;
  }

  someday(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => todo.start.kind === "someday" && !this.isInWaitingProject(todo));
  }

  general(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => this.isLoose(todo) && todo.start.kind !== "inbox" && todo.habitID === undefined);
  }

  /**
   * What can be done now: Today's and Anytime's, not waiting in a project. A habit's days show in Today and in Habits,
   * not in Anytime, an area's list, Miscellaneous or the Archive (docs/63 section 5): thirty "Stretch" rows would bury
   * what is planned there.
   */
  anytime(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => (this.isToday(todo) || todo.start.kind === "anytime") && !this.isInWaitingProject(todo)
      && todo.habitID === undefined);
  }

  /** Habits' days in Today still open: the count beside Habits on Home. */
  get habitsLeftToday(): number {
    return this.todayTodos().filter((todo) => todo.habitID !== undefined).length;
  }

  /** Planned for a later day and not due by today. */
  scheduled(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) =>
      !this.isInWaitingProject(todo) && todo.start.kind === "on" && todo.start.day > this.today && !this.isToday(todo));
  }

  /** To-dos with a deadline, the most urgent first (Someday ones too; not the Inbox's, docs/60, nor those waiting in a project). */
  due(completed: CompletedShown = "none"): Todo[] {
    return this.shown(completed).filter((todo) => todo.deadline !== undefined && todo.start.kind !== "inbox" && !this.isInWaitingProject(todo))
      .sort((a, b) => compareOptional(a.deadline, b.deadline));
  }

  /** How many open to-dos are due today or overdue: the Due count on Home. */
  get dueCount(): number {
    return this.due().filter((todo) => todo.completedAt === undefined && todo.deadline !== undefined && todo.deadline <= this.today).length;
  }

  /** Open to-dos without a date that were added a while ago, the oldest first (in projects of any kind too). */
  get stale(): Todo[] {
    const days = this.options.staleAfterDays ?? STALE_DEFAULT_DAYS;
    return this.data.todos.filter((todo) => isStale(todo, days, this.options.now))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }

  /** The count Home shows beside a built-in list; 0 for the others (Store.count(for:)). */
  count(route: Route): number {
    const open = (todos: Todo[]) => todos.filter((todo) => todo.completedAt === undefined).length;
    switch (route.kind) {
      case "inbox": return open(this.inbox());
      case "today": return open(this.todayTodos());
      case "general": return open(this.general());
      case "due": return this.dueCount;
      case "stale": return this.stale.length;
      case "habits": return this.habitsLeftToday;
      default: return 0;
    }
  }

  /** Open to-dos in a list, for Home's tiles and favorites (Store.openCount(in:)). */
  openCount(route: Route): number {
    return this.sections(route).flatMap((section) => section.todos).filter((todo) => todo.completedAt === undefined).length;
  }

  /** Today's progress: of everything in Today, open or done today, how much is done ("2 of 5"). */
  get todayProgress(): { done: number; total: number } {
    const todos = this.sections({ kind: "today" }, "today").flatMap((section) => section.todos);
    return { done: todos.filter((todo) => todo.completedAt !== undefined).length, total: todos.length };
  }

  /** Completed to-dos older than the free Archive reaches: kept, and shown again with Pro. */
  get hiddenHistoryCount(): number {
    const cutoff = this.historyCutoff;
    if (cutoff === undefined) return 0;
    return this.data.todos.filter((todo) => todo.completedAt !== undefined && dayOf(todo.completedAt, this.options.zone) < cutoff).length;
  }

  private get historyCutoff(): Day | undefined {
    const days = this.options.historyDays;
    return days === undefined ? undefined : addDays(this.today, -days);
  }

  // MARK: Sections

  /**
   * The blocks of a list, with the completed to-dos it is set to show at the end of each block: the open ones (and
   * those just ticked) keep their order, the rest follow in the order they were done (Store.sections(for:completed:)).
   */
  sections(route: Route, completed: CompletedShown = "none"): ListSection[] {
    const sections = this.plainSections(route, completed);
    if (completed === "none") return sections;
    const isDone = (todo: Todo) => todo.completedAt !== undefined && !this.options.lingering?.has(todo.id);
    return sections.map((section) => ({
      ...section,
      todos: [
        ...section.todos.filter((todo) => !isDone(todo)),
        ...section.todos.filter(isDone).sort((a, b) => (a.completedAt?.getTime() ?? 0) - (b.completedAt?.getTime() ?? 0)),
      ],
    }));
  }

  private plainSections(route: Route, completed: CompletedShown): ListSection[] {
    const section = (id: string, header: ListHeader, todos: Todo[], projects: Project[] = []): ListSection => ({ id, header, todos, projects });
    switch (route.kind) {
      case "inbox":
        return [section("inbox", { kind: "none" }, this.inbox(completed))];
      case "today": {
        let items = this.todayTodos(completed);
        // Habits in a group of their own, at the top (docs/63, board 2), when switched on.
        const habitGroup: ListSection[] = [];
        if (this.options.habitsInOwnGroup) {
          const habitDays = items.filter((todo) => todo.habitID !== undefined);
          items = items.filter((todo) => todo.habitID === undefined);
          if (habitDays.length > 0) habitGroup.push(section(TODAY_HABITS_ID, { kind: "text", text: "habits" }, habitDays));
        }
        // Grouped: by project and area, and the user's sections step aside.
        if (this.options.todayGroupsByProject) return [...habitGroup, ...this.grouped(items)];
        // Not grouped (the default): one list, then the user's sections. With a default section, the to-dos in no
        // section show in it rather than above all sections.
        const unsorted = items.filter((todo) => this.todaySectionOf(todo) === undefined);
        const fallback = this.defaultTodaySection;
        // A section that shows nothing (no open to-do, and no completed one where they are shown) steps aside, unless
        // the setting keeps it or it was just made (isNewTodaySection; Bastian, 2026-10-31).
        const own: ListSection[] = [];
        for (const today of this.todaySections) {
          const todos = today.id === fallback?.id
            ? items.filter((todo) => todo.todaySectionID === today.id || this.todaySectionOf(todo) === undefined)
            : items.filter((todo) => todo.todaySectionID === today.id);
          if (todos.length === 0 && !this.options.todayShowsEmptySections && !this.isNewTodaySection(today.id)) continue;
          own.push(section(today.id, { kind: "section", section: today }, todos));
        }
        return [...habitGroup, section("today", { kind: "none" }, fallback === undefined ? unsorted : []), ...own];
      }
      case "scheduled":
        return this.scheduledSections(completed);
      case "anytime":
        return this.grouped(this.anytime(completed));
      case "someday": {
        const waiting = this.somedayProjects;
        const projects = waiting.length === 0 ? [] : [section("someday-projects", { kind: "text", text: "projects" }, [], waiting)];
        return [...projects, ...this.grouped(this.someday(completed))];
      }
      case "archive":
        return this.archiveSections();
      case "due":
        return this.dueSections(completed);
      case "general": {
        const items = this.general(completed);
        const scheduled = new Set(this.scheduled(completed).map((todo) => todo.id));
        const groups: [ListText, Todo[]][] = [
          ["today", items.filter((todo) => this.isToday(todo))],
          ["scheduled", items.filter((todo) => scheduled.has(todo.id))],
          ["anytime", items.filter((todo) => todo.start.kind === "anytime")],
          ["someday", items.filter((todo) => todo.start.kind === "someday")],
        ];
        return groups.filter(([, todos]) => todos.length > 0).map(([text, todos]) => section(`general-${text}`, { kind: "text", text }, todos));
      }
      case "project": {
        const items = this.shown(completed).filter((todo) => todo.projectID === route.id && todo.start.kind !== "inbox");
        return [
          section("project", { kind: "none" }, items.filter((todo) => this.sectionOf(todo) === undefined)),
          ...this.sectionsIn(route.id).map((inner) =>
            section(inner.id, { kind: "section", section: inner }, items.filter((todo) => todo.sectionID === inner.id))),
        ];
      }
      case "area":
        return [section("area", { kind: "none" }, this.shown(completed).filter((todo) =>
          todo.areaID === route.id && todo.projectID === undefined && todo.start.kind !== "inbox" && todo.habitID === undefined))];
      case "search":
        return this.searchSections(route.query);
      case "stale":
        // One list, the oldest first, so the forgotten ones are on top.
        return [section("stale", { kind: "none" }, this.stale)];
      case "place":
        // Place reminders are the iPhone's; the web has no saved places to list.
        return [section("place", { kind: "none" }, [])];
      case "templates": case "template": case "habits":
        // Pages of their own, not lists of to-dos.
        return [];
    }
  }

  /** Projects first, then open to-dos, then what is already done (latest first). Completed projects aren't found. */
  private searchSections(query: string): ListSection[] {
    const needle = query.trim().toLowerCase();
    if (needle === "") return [];
    const projects = this.data.projects.filter((project) => project.completedAt === undefined && project.title.toLowerCase().includes(needle))
      .sort((a, b) => a.order - b.order);
    const found = this.data.todos.filter((todo) => matches(todo, needle)).sort((a, b) => a.order - b.order);
    const open = found.filter((todo) => todo.completedAt === undefined);
    const done = found.filter((todo) => todo.completedAt !== undefined)
      .sort((a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0));
    const sections: ListSection[] = [
      { id: "search-projects", header: { kind: "text", text: "projects" }, todos: [], projects },
      { id: "search-open", header: { kind: "text", text: "todos" }, todos: open, projects: [] },
      { id: "search-done", header: { kind: "text", text: "completed" }, todos: done, projects: [] },
    ];
    return sections.filter((section) => section.todos.length > 0 || section.projects.length > 0);
  }

  /**
   * Groups to-dos by project, or by area when they sit directly in one: the loose ones first, then each area in its
   * order with its own to-dos and then its open projects in their order. To-dos of a completed project, or of a
   * project or area that no area holds, show in no group.
   */
  private grouped(items: readonly Todo[]): ListSection[] {
    const result: ListSection[] = [];
    const loose = items.filter((todo) => this.isLoose(todo));
    if (loose.length > 0) result.push({ id: "loose", header: { kind: "general" }, todos: loose, projects: [] });
    for (const area of this.sortedAreas) {
      const inArea = items.filter((todo) => todo.areaID === area.id && todo.projectID === undefined);
      if (inArea.length > 0) result.push({ id: area.id, header: { kind: "area", area }, todos: inArea, projects: [] });
      for (const project of this.projectsIn(area)) {
        const inProject = items.filter((todo) => todo.projectID === project.id);
        if (inProject.length > 0) result.push({ id: project.id, header: { kind: "project", project }, todos: inProject, projects: [] });
      }
    }
    return result;
  }

  /**
   * The next seven days always, each a place to drop a to-do on, under the name of their month; then one block per
   * month, by date, with no heading per day (from the day after the week in the month it ends in). A month holding
   * only a birthday within the next year gets its block too.
   */
  private scheduledSections(completed: CompletedShown): ListSection[] {
    const byDay = new Map<Day, Todo[]>();
    for (const todo of this.scheduled(completed)) {
      if (todo.start.kind === "on") byDay.set(todo.start.day, [...(byDay.get(todo.start.day) ?? []), todo]);
    }
    const projectsByDay = new Map<Day, Project[]>();
    for (const project of this.scheduledProjects) {
      if (project.start?.kind === "on") projectsByDay.set(project.start.day, [...(projectsByDay.get(project.start.day) ?? []), project]);
    }
    const monthStart = (day: Day) => addDays(day, 1 - dayParts(day).date);

    const result: ListSection[] = [];
    const week = Array.from({ length: 7 }, (_, index) => addDays(this.today, index + 1));
    let titled: Day | undefined;
    for (const day of week) {
      const month = monthStart(day);
      if (month !== titled) {
        result.push({ id: `month-title-${month}`, header: { kind: "monthTitle", day: month }, todos: [], projects: [] });
        titled = month;
      }
      result.push({ id: `day-${day}`, header: { kind: "day", day }, todos: byDay.get(day) ?? [], projects: projectsByDay.get(day) ?? [] });
    }

    const afterWeek = addDays(this.today, 8);
    const yearAhead = addDays(this.today, 365);
    const birthdayDays = this.birthdaysAreOn
      ? this.shownBirthdays.map((birthday) => nextBirthday(birthday, afterWeek)).filter((day) => day < yearAhead)
      : [];
    const laterDays = [...new Set([...byDay.keys(), ...projectsByDay.keys()])].filter((day) => day >= afterWeek).sort();
    const months = [...new Set([...laterDays, ...birthdayDays].map(monthStart))].sort();
    for (const month of months) {
      const days = laterDays.filter((day) => monthStart(day) === month);
      result.push({
        id: `month-${month}`, header: { kind: "month", day: maxDay(month, afterWeek) },
        todos: days.flatMap((day) => byDay.get(day) ?? []), projects: days.flatMap((day) => projectsByDay.get(day) ?? []),
      });
    }
    return result;
  }

  /** Deadlines of to-dos and projects (not lists), sorted by date: what is late, what is due today, what comes later. */
  private dueSections(completed: CompletedShown): ListSection[] {
    const items = this.due(completed);
    const projects = this.data.projects.filter((project) => project.completedAt === undefined && activeDeadline(project) !== undefined)
      .sort((a, b) => compareOptional(activeDeadline(a), activeDeadline(b)));
    const today = this.today;
    const groups: [ListText, (day: Day) => boolean][] = [
      ["overdue", (day) => day < today], ["due", (day) => day === today], ["upcomingDeadlines", (day) => day > today],
    ];
    return groups.flatMap(([text, holds]) => {
      const todos = items.filter((todo) => todo.deadline !== undefined && holds(todo.deadline));
      const inside = projects.filter((project) => project.deadline !== undefined && holds(project.deadline));
      return todos.length === 0 && inside.length === 0 ? [] : [{ id: `due-${text}`, header: { kind: "text", text } as ListHeader, todos, projects: inside }];
    });
  }

  /** Whether a to-do belongs to the Archive's filter: in that project, or in that area (also through its project). */
  matchesArchiveFilter(todo: Todo): boolean {
    const filter = this.options.archiveFilter;
    if (filter === undefined) return true;
    if (filter.kind === "project") return todo.projectID === filter.id;
    return todo.areaID === filter.id || this.project(todo.projectID)?.areaID === filter.id;
  }

  /** Completed to-dos by the day they were done, the latest first, as far back as the Archive reaches. */
  private archiveSections(): ListSection[] {
    const cutoff = this.historyCutoff;
    const zone = this.options.zone;
    const done = this.data.todos
      // A habit's days are its history, on its page, not the Archive's (docs/63 section 5).
      .filter((todo) => todo.completedAt !== undefined && todo.habitID === undefined && (cutoff === undefined || dayOf(todo.completedAt, zone) >= cutoff)
        && this.matchesArchiveFilter(todo))
      .sort((a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0));
    const byDay = new Map<Day, Todo[]>();
    for (const todo of done) {
      const day = dayOf(todo.completedAt ?? new Date(0), zone);
      byDay.set(day, [...(byDay.get(day) ?? []), todo]);
    }
    return [...byDay.keys()].sort().reverse().map((day) => ({ id: `log-${day}`, header: { kind: "day", day }, todos: byDay.get(day) ?? [], projects: [] }));
  }

  // MARK: Birthdays

  /** The list of birthdays, if the user has one (there is only ever one). */
  get birthdaysList(): Project | undefined {
    return this.data.projects.find((project) => project.kind === "birthdays" && project.completedAt === undefined);
  }

  /** Birthdays show only while their list exists and is switched on. */
  get birthdaysAreOn(): boolean {
    const list = this.birthdaysList;
    return list !== undefined && list.isOff !== true;
  }

  /** Every birthday but the hidden ones. */
  get shownBirthdays(): Birthday[] {
    return (this.data.birthdays ?? []).filter((birthday) => birthday.isHidden !== true);
  }

  /** The birthdays on a day, by name (Store.birthdays(on:)). */
  birthdaysOn(day: Day): Birthday[] {
    if (!this.birthdaysAreOn) return [];
    const year = dayParts(day).year;
    return this.shownBirthdays.filter((birthday) => birthdayIn(birthday, year) === day).sort((a, b) => compareText(a.name, b.name));
  }

  /** The birthdays from `start` to the end of its month, each on its day, for a month in Scheduled. */
  birthdaysInMonthFrom(start: Day): BirthdayDay[] {
    if (!this.birthdaysAreOn) return [];
    const { year, month } = dayParts(start);
    const end = `${String(month === 12 ? year + 1 : year).padStart(4, "0")}-${String(month === 12 ? 1 : month + 1).padStart(2, "0")}-01` as Day;
    return this.shownBirthdays.map((birthday) => ({ birthday, day: nextBirthday(birthday, start) }))
      .filter((entry) => entry.day < end)
      .sort((a, b) => (a.day === b.day ? compareText(a.birthday.name, b.birthday.name) : a.day < b.day ? -1 : 1));
  }
}

/** Title, notes, tags and checklist items, for a needle in lower case (Todo.matches). */
export function matches(todo: Todo, needle: string): boolean {
  return todo.title.toLowerCase().includes(needle)
    || todo.notes.toLowerCase().includes(needle)
    || todo.tags.some((tag) => tag.toLowerCase().includes(needle))
    || todo.checklist.some((item) => item.title.toLowerCase().includes(needle));
}

function compareText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
